import type { AnnouncementSwatch, SessionEntry } from '@accounts/shared';

/**
 * Session-row presentation helpers, shared by the two surfaces that render devices: the
 * admin Sessions screen and the self-service Security page. One home, because the two
 * screens must agree on what "the apps this device has been seen in" means — two copies of
 * this dedupe is two chances for one of them to start counting differently.
 */

/** One application a device has been seen entering, as a row renders it. */
export interface SeenApp {
  id: string;
  name: string;
  /**
   * The application's pill colour, resolved SERVER-SIDE on the sessions read
   * (`applications.pill_color`). Null = no colour assigned = a neutral chip, never a
   * guessed default. Nothing in /web maps a colour here; it is read as it arrives.
   */
  color: AnnouncementSwatch | null;
}

/**
 * Distinct apps across a set of sessions, keyed by id — IDENTITY IS A FOREIGN KEY, never
 * the display name — in first-seen order, which is the server's own order for the rows.
 *
 * THE STANDING CARVE-OUT lives at the call sites, not here: an empty result renders NO apps
 * segment at all on either surface. Never "0 apps", never an empty placeholder.
 */
export function distinctApps(sessions: readonly SessionEntry[]): SeenApp[] {
  const seen = new Map<string, SeenApp>();
  for (const session of sessions) {
    for (const app of session.apps) {
      if (!seen.has(app.id)) seen.set(app.id, { id: app.id, name: app.name, color: app.color });
    }
  }
  return [...seen.values()];
}
